import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { BACKLOG_CARD_TYPES, SPRINT_STATUSES, PROJECT_TYPES, BACKEND_SCRUM_ROLES } from '../../shared/appConstants';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  Badge,
  MenuItem, 
  Menu,
  Select,
  Tooltip,
  Collapse
} from '@mui/material';
import { 
  Refresh, 
  Edit, 
  Add,
  Close,
  WarningAmber,
  FlagOutlined
} from '@mui/icons-material';
import Navbar from '../shared/Navbar';
import HeaderProfileMenu from '../shared/HeaderProfileMenu';
import { toast } from 'react-toastify';
import AddTaskModal from '../dashboard/AddTaskModal';
import BacklogMetricCards from './components/BacklogMetricCards';
import BacklogSidebar from './components/BacklogSidebar';
import { fetchTicketDropdowns, fetchProjectDropdownsByUser, setFilters, createTicket, updateTicketPartial, saveUserPreference, fetchLastSelectedProject } from '../../store/ticketSlice';
import { fetchScrumDashboard, fetchSprintsTickets, fetchScrumRole,
  clearScrumData, fetchMyScrumTickets, updateSprintStatus } from '../../store/scrumSlice';
import { USER_ROLES, NOTIFICATION_BADGE_THRESHOLDS } from '../../shared/appConstants';
import { getOrdinalSuffix } from '../../shared/commonFunctions';
import { COLORS } from '../../shared/colors';
import { getUnassignedPriority } from '../../shared/priorityUtils';

import { api } from '../../services';
import { fetchUnreadCount } from '../../store/notificationSlice';
import { createNotificationSoundManager } from '../../shared/audioUtils';
import bellIcon from '../../assets/images/bell_icon.svg';
import './BacklogManagement.scss';


import BacklogTable from './components/BacklogTable';
import SprintModal from '../../components/SprintModal';
import ScrumWarningModal from '../../components/ScrumWarningModal';
import OverflowTooltip from '../../shared/OverflowTooltip';

const formatDateRange = (startStr, endStr, useOrdinal = false) => {
  if (!startStr || !endStr) return 'TBD';
  
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const parseDate = (str) => {
    if (!str) return null;
    const parts = str.split('-');
    if (parts.length === 3) {
      const monthIndex = parseInt(parts[1], 10) - 1;
      const shortMonth = months[monthIndex] ? months[monthIndex].slice(0, 4) : '';
      const formattedMonth = shortMonth === 'June' ? 'June' : (months[monthIndex] ? months[monthIndex].slice(0, 3) : '');
      const day = parseInt(parts[2], 10);
      return {
        day: day,
        dayStr: useOrdinal ? `${day}${getOrdinalSuffix(day)}` : `${day}`,
        month: formattedMonth,
        year: parts[0]
      };
    }
    return null;
  };

  const start = parseDate(startStr);
  const end = parseDate(endStr);

  if (start && end) {
    return `${start.dayStr} ${start.month} - ${end.dayStr} ${end.month}`;
  }
  return `${startStr} - ${endStr}`;
};

const BacklogManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth state
  const { user } = useSelector((state) => state.auth);
  const currentUserInitials = user?.initials || user?.id || '';
  const userRole = user?.role || USER_ROLES.USER;
  const { projectDropdownsByUser, projectDropdownsByUserLoading, filters, dropdowns, lastSelectedProjectId } = useSelector((state) => state.tickets);
  const { dashboard, dashboardLoading, sprintsTickets, sprintsTicketsLoading, myTickets: apiMyTickets, myTicketsLoading, scrumRole } = useSelector(state => state.scrum);
  
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    const pId = filters?.selectedProject;
    return (pId && String(pId).toLowerCase() !== 'all') ? pId : '';
  });
  
  const selectedProject = projectDropdownsByUser?.find(p => p.id === selectedProjectId);
  
  // Use API response if available, otherwise fallback to project dropdown data for instant initial state
  const isScrumMaster = scrumRole 
    ? scrumRole.scrum_role === BACKEND_SCRUM_ROLES.SCRUM_MASTER 
    : (selectedProject?.scrum_master?.id === user?.id || selectedProject?.scrum_master === user?.id);
    
  const isProductOwner = scrumRole 
    ? scrumRole.scrum_role === BACKEND_SCRUM_ROLES.PRODUCT_OWNER 
    : (selectedProject?.product_owner?.some(po => po.id === user?.id || po === user?.id));
  
  const isAuthorized = isScrumMaster || isProductOwner;

  // Selected Card state: BACKLOG_CARD_TYPES.ACTIVE_SPRINT or BACKLOG_CARD_TYPES.PLANNED_SPRINT or BACKLOG_CARD_TYPES.MY_TICKETS
  const [selectedCardType, setSelectedCardType] = useState(isAuthorized ? BACKLOG_CARD_TYPES.ACTIVE_SPRINT : BACKLOG_CARD_TYPES.MY_TICKETS);
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);

  // Filters & Sorting state (moved up for dependency in useEffect)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortOption, setSortOption] = useState('DEFAULT'); // 'DEFAULT', 'PRIORITY_HIGH_TO_LOW', 'SP_LOW_TO_HIGH'


  // Notification state
  const { unreadCount, unreadCountByProject } = useSelector((state) => state.notifications);
  const currentUnreadCount = (selectedProjectId && selectedProjectId !== 'ALL' && selectedProjectId !== 'all')
    ? (unreadCountByProject?.[selectedProjectId] || 0)
    : unreadCount;
  const previousUnreadCountRef = useRef(0);
  const [bellRinging, setBellRinging] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const [audioPromptReady] = useState(false);
  const activeNotificationProjectIdRef = useRef(null);
  const getNotificationBadgeLabel = useCallback((count) => {
    if (!count || count < 1) return '';
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.HUNDRED) {
      return t('dashboard.notificationsBadge.ninetyNinePlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.FIFTY) {
      return t('dashboard.notificationsBadge.fiftyPlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.THIRTY) {
      return t('dashboard.notificationsBadge.thirtyPlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.TWENTY) {
      return t('dashboard.notificationsBadge.twentyPlus');
    }
    if (count >= NOTIFICATION_BADGE_THRESHOLDS.TEN) {
      return t('dashboard.notificationsBadge.ninePlus');
    }
    return String(count);
  }, [t]);

  const notificationBadgeLabel = useMemo(
    () => getNotificationBadgeLabel(currentUnreadCount),
    [currentUnreadCount, getNotificationBadgeLabel]
  );

  const notificationSoundManager = useMemo(
    () => createNotificationSoundManager({
      getProjectId: () => activeNotificationProjectIdRef.current
    }),
    []
  );

  useEffect(() => {
    notificationSoundManager.syncDebugState();
  }, [notificationSoundManager]);

  // Fetch project-specific unread notification count
  useEffect(() => {
    const userId = user?.id || user?.user_id;
    const hasSelectedProject = selectedProjectId && String(selectedProjectId).toUpperCase() !== 'ALL';
    if (userId && hasSelectedProject) {
      dispatch(fetchUnreadCount({ userId, projectId: selectedProjectId }));
    }
  }, [dispatch, selectedProjectId, user?.id, user?.user_id]);

  useEffect(() => {
    activeNotificationProjectIdRef.current = selectedProjectId && selectedProjectId !== 'ALL'
      ? selectedProjectId
      : null;
    notificationSoundManager.syncDebugState();
  }, [notificationSoundManager, selectedProjectId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('audioEnabledThisSession');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Keep a ref to the current selected project for the bell ringing check
  const selectedProjectIdRef = useRef(selectedProjectId);
  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  useEffect(() => {
    const handleNotificationEvent = (event) => {
      const { newNotificationCount, unreadCount, notificationData } = event?.detail || {};
      if (typeof unreadCount === 'number') {
        previousUnreadCountRef.current = unreadCount;
      }
      if (newNotificationCount > 0) {
        // Only ring bell if notification belongs to selected project (or no project filter)
        const currentProjectId = selectedProjectIdRef.current;
        const notifProjectId = notificationData?.projectId;
        const isAllProjects = !currentProjectId || currentProjectId.toUpperCase() === 'ALL';
        const isMatchingProject = isAllProjects || notifProjectId === currentProjectId;
        if (isMatchingProject) {
          setBellRinging(true);
          setTimeout(() => setBellRinging(false), 2000);
        }
      }
    };

    window.addEventListener('pmpi:new-notification', handleNotificationEvent);
    return () => {
      window.removeEventListener('pmpi:new-notification', handleNotificationEvent);
    };
  }, []);
  // Fetch initial project options and preference
  useEffect(() => {
    dispatch(fetchProjectDropdownsByUser());
    dispatch(fetchLastSelectedProject()).finally(() => {
      setPreferenceLoaded(true);
    });
  }, [dispatch]);


  // Set default selected project or clear invalid ones
  useEffect(() => {
    if (projectDropdownsByUserLoading || !preferenceLoaded) return;
    if (projectDropdownsByUser && projectDropdownsByUser.length > 0) {
      if (!selectedProjectId || String(selectedProjectId).toUpperCase() === 'ALL') {
        let projectToSelect = lastSelectedProjectId
          ? projectDropdownsByUser.find(p => String(p.id) === String(lastSelectedProjectId))
          : null;
        
        // Only select Scrum projects, as this is the Backlog dashboard
        if (projectToSelect && projectToSelect.project_type?.toUpperCase() !== PROJECT_TYPES.SCRUM.toUpperCase()) {
          projectToSelect = null;
        }

        if (projectToSelect) {
          setSelectedProjectId(projectToSelect.id);
        } else {
          const scrumProjects = projectDropdownsByUser.filter(p => (p.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase());
          if (scrumProjects.length > 0) {
            setSelectedProjectId(scrumProjects[0].id);
          }
        }
      } else {
        // If a project is selected but it NO LONGER EXISTS in the dropdown (e.g. was deleted)
        const projectExists = projectDropdownsByUser.some(p => String(p.id) === String(selectedProjectId));
        if (!projectExists) {
          setSelectedProjectId('');
          dispatch(clearScrumData());
        }
      }
    }
  }, [projectDropdownsByUser, selectedProjectId, dispatch, preferenceLoaded, projectDropdownsByUserLoading, lastSelectedProjectId]);

  // Ensure Scrum Backlog Management only allows Scrum projects
  useEffect(() => {
    if (!projectDropdownsByUserLoading && projectDropdownsByUser?.length > 0) {
      if (selectedProjectId && String(selectedProjectId).toLowerCase() !== 'all') {
        const project = projectDropdownsByUser.find(p => String(p.id) === String(selectedProjectId));
        if (project && project.project_type?.toUpperCase() === PROJECT_TYPES.KANBAN.toUpperCase()) {
          navigate('/ticket-management', { replace: true });
        }
      } else {
        // No valid project selected. If they have NO Scrum projects but DO have Kanban projects, redirect them away from Backlog
        const hasScrumProjects = projectDropdownsByUser.some(p => (p.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase());
        const hasKanbanProjects = projectDropdownsByUser.some(p => (p.project_type || '').toUpperCase() === PROJECT_TYPES.KANBAN.toUpperCase());
        if (!hasScrumProjects && hasKanbanProjects) {
          navigate('/ticket-management', { replace: true });
        }
      }
    }
  }, [projectDropdownsByUserLoading, projectDropdownsByUser, selectedProjectId, navigate]);

  const getSprintStateFromCardType = useCallback((type) => {
    switch(type) {
      case BACKLOG_CARD_TYPES.ACTIVE_SPRINT: return 'ACTIVE';
      case BACKLOG_CARD_TYPES.PLANNED_SPRINT: return 'DRAFT';
      case BACKLOG_CARD_TYPES.MY_TICKETS: return 'BACKLOG';
      default: return 'ACTIVE';
    }
  }, []);

  // Fetch dashboard, dropdowns, and role when project changes
  useEffect(() => {
    if (selectedProjectId) {
      dispatch(clearScrumData());
      setTickets([]);
      setSprints([]);
      setInitialAuthSynced(false);
      lastSyncedSprintsTickets.current = null;
      lastSyncedMyTickets.current = null;
      
      // Reset view state so the new project loads its active sprint correctly
      setSelectedCardType(BACKLOG_CARD_TYPES.MY_TICKETS);
      setCurrentMainView('my_tickets');

      dispatch(fetchTicketDropdowns(selectedProjectId));
      dispatch(fetchScrumDashboard(selectedProjectId));
      dispatch(fetchScrumRole(selectedProjectId));
      dispatch(setFilters({ selectedProject: selectedProjectId }));
    }
  }, [selectedProjectId, dispatch]);

  const handleRefresh = () => {
    if (selectedProjectId) {
      dispatch(fetchTicketDropdowns(selectedProjectId));
      dispatch(fetchScrumDashboard(selectedProjectId));
      dispatch(fetchScrumRole(selectedProjectId));
      
      // Clear fetch signature and reset pagination to trigger a clean data refetch
      lastFetchedParamsRef.current = null;
      lastFetchSignatureRef.current = null;
      setCurrentPage(1);
    }
  };

  // Sprints state
  const [sprints, setSprints] = useState([]);

  // Save Sprints changes
  const saveSprints = (newSprints) => {
    setSprints(newSprints);
  };

  // Active Sprint
  const activeSprint = useMemo(() => sprints.find(s => s.status === SPRINT_STATUSES.ACTIVE) || null, [sprints]);

  // Single main view configuration: 'sprint-1', 'sprint-2', 'sprint-3', or 'backlog'
  const [currentMainView, setCurrentMainView] = useState(() => 
    !isAuthorized ? 'my_tickets' : (sprints.find(s => s.status === SPRINT_STATUSES.ACTIVE)?.id || 'sprint-1')
  );

  const [initialAuthSynced, setInitialAuthSynced] = useState(false);

  useEffect(() => {
    if (isAuthorized && !initialAuthSynced) {
      if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
        setSelectedCardType(BACKLOG_CARD_TYPES.ACTIVE_SPRINT);
      }
      if (currentMainView === 'my_tickets') {
        setCurrentMainView(sprints.find(s => s.status === SPRINT_STATUSES.ACTIVE)?.id || 'sprint-1');
      }
      setInitialAuthSynced(true);
    }
  }, [isAuthorized, initialAuthSynced, selectedCardType, currentMainView, sprints]);

  const handleSetSelectedCardType = useCallback((type) => {
    setSelectedCardType(type);
    setSortOption('DEFAULT');
  }, []);

  const handleSetCurrentMainView = useCallback((view) => {
    setCurrentMainView(view);
    setSortOption('DEFAULT');
  }, []);
  const [dragOverCardId, setDragOverCardId] = useState(null);

  // Clear search on tab/project change
  useEffect(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, [selectedProjectId, currentMainView, selectedCardType]);

  const currentMainSprint = useMemo(() => {
    if (currentMainView === 'backlog') return null;
    if (currentMainView === 'my_tickets') return activeSprint;
    const foundSprint = sprints.find(s => s.id === currentMainView);
    if (foundSprint) return foundSprint;
    if (selectedCardType === BACKLOG_CARD_TYPES.ACTIVE_SPRINT) return activeSprint;
    if (selectedCardType === BACKLOG_CARD_TYPES.PLANNED_SPRINT) {
      return sprints.find(s => s.status === SPRINT_STATUSES.PLANNED) || null;
    }
    return null;
  }, [sprints, currentMainView, activeSprint, selectedCardType]);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTickets, setHasMoreTickets] = useState(true);


  const lastFetchedParamsRef = useRef(null);
  const lastFetchSignatureRef = useRef(null);
  const lastCoreParamsRef = useRef(null);
  const isCoreViewChange = useRef(true);

  // Unified Effect: Handle parameter changes, reset page if needed, and fetch data
  useEffect(() => {
    if (!selectedProjectId) return;
    if (scrumRole === null) return;
    
    // Prevent fetching my_tickets if auth sync is about to switch it to active_sprint
    if (isAuthorized && !initialAuthSynced && selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
      return;
    }

    let state = getSprintStateFromCardType(selectedCardType);
    let sprintId = null;

    if (currentMainView === 'backlog') {
      state = 'BACKLOG';
    } else if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
      state = 'my_tickets';
    } else if (state === 'DRAFT') {
      sprintId = dashboard?.upcoming_sprint?.id || dashboard?.planned_sprint?.id;
      if (!sprintId) {
        if (dashboardLoading) return;
        setTickets(prev => prev.filter(t => t.sprintId === null));
        setSprints([]);
        state = 'BACKLOG';
      }
    }

    let sortParam = '';
    if (sortOption === 'PRIORITY_HIGH_TO_LOW') sortParam = 'priority';
    else if (sortOption === 'SP_LOW_TO_HIGH') sortParam = 'story';

    const currentParamsStr = JSON.stringify({ projectId: selectedProjectId, state, sprintId, search: debouncedSearchTerm, sort: sortParam });
    const coreParamsStr = JSON.stringify({ projectId: selectedProjectId, state, sprintId });

    if (lastCoreParamsRef.current !== coreParamsStr) {
      lastCoreParamsRef.current = coreParamsStr;
      isCoreViewChange.current = true;
    }

    let fetchPage = currentPage;

    // Check if params changed (excluding page)
    if (lastFetchedParamsRef.current !== currentParamsStr) {
      lastFetchedParamsRef.current = currentParamsStr;
      fetchPage = 1;
      setCurrentPage(1); 
    }

    const currentFetchSignature = JSON.stringify({ paramsStr: currentParamsStr, page: fetchPage });
    if (lastFetchSignatureRef.current === currentFetchSignature) {
      // Already fetched this exact combination
      return;
    }
    
    lastFetchSignatureRef.current = currentFetchSignature;

    if (state === 'my_tickets') {
      dispatch(fetchMyScrumTickets({ projectId: selectedProjectId, page: fetchPage, limit: 15, search: debouncedSearchTerm, sort: sortParam }));
    } else {
      dispatch(fetchSprintsTickets({ projectId: selectedProjectId, state, sprintId, page: fetchPage, limit: 15, search: debouncedSearchTerm, sort: sortParam }));
    }
  }, [selectedProjectId, selectedCardType, currentMainView, dashboard, dashboardLoading, dispatch, currentPage, debouncedSearchTerm, sortOption, getSprintStateFromCardType, scrumRole, isAuthorized, initialAuthSynced]);

  // Tickets state
  const [tickets, setTickets] = useState([]);

  // Save Tickets changes
  const saveTickets = (newTickets) => {
    setTickets(newTickets);
  };

  // Keep track of the total backlog count because the API omits it for DRAFT sprint responses
  const [lastKnownTotalBacklog, setLastKnownTotalBacklog] = useState(0);

  useEffect(() => {
    if (sprintsTickets?.backlog?.total !== undefined) {
      setLastKnownTotalBacklog(sprintsTickets.backlog.total);
    } else if (sprintsTickets?.total !== undefined && !sprintsTickets.active_sprint && !sprintsTickets.upcoming_sprint && !sprintsTickets.id) {
      setLastKnownTotalBacklog(sprintsTickets.total);
    }
  }, [sprintsTickets]);

  // Clear data when selectedProjectId changes
  useEffect(() => {
    if (selectedProjectId) {
      setTickets([]);
      setSprints([]);
      setCurrentPage(1);
      setHasMoreTickets(true);
      lastSyncedSprintsTickets.current = null;
      lastSyncedMyTickets.current = null;
      setLastKnownTotalBacklog(0);
    } else {
      setTickets([]);
      setSprints([]);
      setLastKnownTotalBacklog(0);
    }
  }, [selectedProjectId]);

  // Memoize the transformation function
  const transformSprintsTickets = useCallback((data, cardType) => {
    const newSprints = [];
    const newTickets = [];
    let isBacklogOnly = false;

    const mapStoryPoints = (storyPoints = {}) => ({
      complete_story_points: Number(storyPoints.completed || 0),
      incomplete_story_points: Number(storyPoints.incomplete || 0),
      committed_story_points: Number(storyPoints.committed || 0),
      completeStoryPoints: Number(storyPoints.completed || 0),
      incompleteStoryPoints: Number(storyPoints.incomplete || 0),
      committedStoryPoints: Number(storyPoints.committed || 0)
    });
    
    if (!data || (!data.id && !data.tickets && !data.active_sprint && !data.upcoming_sprint && !data.backlog)) {
      return { newSprints, newTickets, isBacklogOnly };
    }

    const pushTickets = (ticketList, defaultSprintId) => {
      if (Array.isArray(ticketList)) {
        ticketList.forEach(t => {
          if (t) {
            newTickets.push({
              ...t,
              id: t.id || t.ticket_code,
              code: t.code || t.ticket_code,
              sp: Number(t.sp !== undefined ? t.sp : (t.story_point || 0)),
              assignee: (t.assignee && t.assignee.length > 0) ? (typeof t.assignee === 'string' ? t.assignee : t.assignee[0].name) : '',
              assignees: Array.isArray(t.assignee) ? t.assignee : (t.assignees || []),
              sprintId: t.sprintId || defaultSprintId
            });
          }
        });
      }
    };

    // Handle legacy format
    if (data.active_sprint) {
      newSprints.push({
        id: data.active_sprint.id,
        name: data.active_sprint.sprint_name,
        startDate: data.active_sprint.start_date,
        endDate: data.active_sprint.end_date,
        status: SPRINT_STATUSES.ACTIVE,
        goal: data.active_sprint.goal,
        capacity: Number(data.active_sprint.max_story_points || 0),
        totalPoints: Number(data.active_sprint.total_story_points || 0),
        completedStory: Number(data.active_sprint.completed_story || 0),
        remainingDays: data.active_sprint.remaining_days,
        completedPercentage: data.active_sprint.completed_percentage,
        pendingP1Count: data.active_sprint.pending_p1_count,
        incompleteTicketCount: data.active_sprint.incomplete_ticket_count
      });
      pushTickets(data.active_sprint.tickets, data.active_sprint.id);
    }
    
    if (data.upcoming_sprint) {
      newSprints.push({
        id: data.upcoming_sprint.id,
        name: data.upcoming_sprint.name,
        startDate: data.upcoming_sprint.start_date,
        endDate: data.upcoming_sprint.end_date,
        status: SPRINT_STATUSES.PLANNED,
        goal: data.upcoming_sprint.goal,
        capacity: Number(data.upcoming_sprint.max_story_points || 0),
        totalPoints: Number(data.upcoming_sprint.total_story_points || 0),
        completedStory: Number(data.upcoming_sprint.completed_story || 0)
      });
      pushTickets(data.upcoming_sprint.tickets, data.upcoming_sprint.id);
    }

    if (data.backlog?.tickets && (data.active_sprint || data.upcoming_sprint)) {
      pushTickets(data.backlog.tickets, null);
    }

    // Handle NEW format
    if (data.id && data.name) {
      const isDraft = getSprintStateFromCardType(cardType) === 'DRAFT' || !data.start_date;
      const storyPoints = mapStoryPoints(data.details?.health?.story_points);
      
      newSprints.push({
        id: data.id,
        name: data.name,
        startDate: data.start_date,
        endDate: data.end_date,
        status: isDraft ? SPRINT_STATUSES.PLANNED : SPRINT_STATUSES.ACTIVE,
        goal: data.goal,
        capacity: Number(data.max_story_points || 0),
        totalPoints: Number(data.total_story_points || 0),
        completedStory: Number(data.completed_story || 0),
        ...storyPoints,
        remainingDays: data.remaining_days,
        completedPercentage: data.completed_percentage,
        pendingP1Count: data.pending_p1_count,
        incompleteTicketCount: data.incomplete_ticket_count
      });
      pushTickets(data.tickets, data.id);
      
      if (data.backlog?.tickets) {
        pushTickets(data.backlog.tickets, null);
      }
      
      if (Array.isArray(data.draft_sprints)) {
        data.draft_sprints.forEach(ds => {
          newSprints.push({
            id: ds.id,
            name: ds.name,
            startDate: ds.start_date,
            endDate: ds.end_date,
            status: SPRINT_STATUSES.PLANNED,
            goal: ds.goal,
            capacity: Number(ds.max_story_points || 0),
            totalPoints: Number(ds.total_story_points || 0),
            remainingDays: ds.remaining_days
          });
          if (Array.isArray(ds.tickets)) {
            pushTickets(ds.tickets, ds.id);
          }
        });
      }
    } 
    else if (!data.active_sprint && !data.upcoming_sprint && !data.id) {
      if (data.tickets) {
        pushTickets(data.tickets, null);
      }
      if (data.backlog?.tickets) {
        pushTickets(data.backlog.tickets, null);
      }
      isBacklogOnly = true;
    }

    return { newSprints, newTickets, isBacklogOnly };
  }, [getSprintStateFromCardType]);

  // Sync state from API response
  useEffect(() => {
    if (sprintsTickets && sprintsTickets !== lastSyncedSprintsTickets.current) {
      const { newSprints, newTickets, isBacklogOnly } = transformSprintsTickets(sprintsTickets, selectedCardType);
      const isEmptyActiveSprintResponse = selectedCardType === BACKLOG_CARD_TYPES.ACTIVE_SPRINT
        && Object.prototype.hasOwnProperty.call(sprintsTickets, 'id')
        && !sprintsTickets.id;

      if (isEmptyActiveSprintResponse) {
        setSprints(prev => prev.filter(s => s.status !== SPRINT_STATUSES.ACTIVE));
        if (currentPage === 1) {
          setCurrentMainView(null);
        }
        setTickets(newTickets);
        const hasMore = (sprintsTickets.tickets?.length === 15) ||
                        (sprintsTickets.backlog?.tickets?.length === 15);
        setHasMoreTickets(hasMore);
      } else if (isBacklogOnly) {
        setTickets(prev => {
          const existingSprintTickets = prev.filter(t => t.sprintId !== null);
          const currentBacklogTickets = currentPage > 1 ? prev.filter(t => t.sprintId === null) : [];
          return [...existingSprintTickets, ...currentBacklogTickets, ...newTickets];
        });
        const hasMore = (sprintsTickets.tickets?.length === 15) || 
                        (sprintsTickets.active_sprint?.tickets?.length === 15) ||
                        (sprintsTickets.upcoming_sprint?.tickets?.length === 15) ||
                        (sprintsTickets.backlog?.tickets?.length === 15);
        setHasMoreTickets(hasMore);
      } else {
        if (newSprints.length > 0) {
          setSprints(newSprints);
          
          if (currentPage === 1) {
            if (selectedCardType === BACKLOG_CARD_TYPES.PLANNED_SPRINT) {
              const plannedSprint = newSprints.find(s => s.status === SPRINT_STATUSES.PLANNED);
              if (plannedSprint && plannedSprint.id) {
                setCurrentMainView(plannedSprint.id);
              }
            }
            if (selectedCardType === BACKLOG_CARD_TYPES.ACTIVE_SPRINT) {
              const activeSp = newSprints.find(s => s.status === SPRINT_STATUSES.ACTIVE);
              if (activeSp && activeSp.id) {
                setCurrentMainView(activeSp.id);
              }
            }
          }
        }
        // Even if newTickets is empty, we set it if API returned valid objects
        setTickets(prev => {
          if (currentPage > 1) {
            return [...prev, ...newTickets];
          }
          
          // If the API response did not include a backlog (like for DRAFT sprints), 
          // we must preserve the previously fetched backlog tickets.
          if (!sprintsTickets.backlog) {
            const existingBacklogTickets = prev.filter(t => t.sprintId === null);
            return [...existingBacklogTickets, ...newTickets];
          }
          
          return newTickets;
        });
        const hasMore = (sprintsTickets.tickets?.length === 15) || 
                        (sprintsTickets.active_sprint?.tickets?.length === 15) ||
                        (sprintsTickets.upcoming_sprint?.tickets?.length === 15) ||
                        (sprintsTickets.backlog?.tickets?.length === 15);
        setHasMoreTickets(hasMore);
      }
      
      // Update ref to indicate sync is complete
      lastSyncedSprintsTickets.current = sprintsTickets;
    }
  }, [sprintsTickets, currentPage, selectedCardType, transformSprintsTickets]);

  // Sync state from apiMyTickets response
  useEffect(() => {
    if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS && apiMyTickets && apiMyTickets !== lastSyncedMyTickets.current) {
      if (apiMyTickets.active_sprint && currentPage === 1) {
        const storyPoints = apiMyTickets.details?.health?.story_points || {};
        setSprints([{
          id: apiMyTickets.active_sprint.id,
          name: apiMyTickets.active_sprint.name || apiMyTickets.active_sprint.sprint_name || 'Sprint',
          startDate: apiMyTickets.active_sprint.start_date,
          endDate: apiMyTickets.active_sprint.end_date,
          status: SPRINT_STATUSES.ACTIVE,
          goal: apiMyTickets.active_sprint.goal,
          capacity: Number(apiMyTickets.active_sprint.max_story_points || 0),
          totalPoints: Number(apiMyTickets.active_sprint.total_story_points || 0),
          remainingDays: apiMyTickets.active_sprint.remaining_days,
          completedPercentage: apiMyTickets.active_sprint.completed_percentage,
          pendingP1Count: apiMyTickets.active_sprint.pending_p1_count,
          incompleteTicketCount: apiMyTickets.active_sprint.incomplete_ticket_count,
          completedStory: Number(apiMyTickets.active_sprint.completed_story || 0),
          complete_story_points: Number(storyPoints.completed || 0),
          incomplete_story_points: Number(storyPoints.incomplete || 0),
          committed_story_points: Number(storyPoints.committed || 0),
          completeStoryPoints: Number(storyPoints.completed || 0),
          incompleteStoryPoints: Number(storyPoints.incomplete || 0),
          committedStoryPoints: Number(storyPoints.committed || 0)
        }]);
      }

      const newTickets = [];

      if (apiMyTickets.tickets) {
        newTickets.push(...apiMyTickets.tickets.map(t => ({
          ...t,
          id: t.id || t.ticket_code,
          code: t.code || t.ticket_code,
          sp: Number(t.sp !== undefined ? t.sp : (t.story_point || 0)),
          assignee: (t.assignee && t.assignee.length > 0) ? (typeof t.assignee === 'string' ? t.assignee : t.assignee[0].name) : '',
          assignees: Array.isArray(t.assignee) ? t.assignee : (t.assignees || []),
          sprintId: apiMyTickets.active_sprint?.id || activeSprint?.id || null
        })));
      }

      if (apiMyTickets.backlog?.tickets && currentPage === 1) {
        newTickets.push(...apiMyTickets.backlog.tickets.map(t => ({
          ...t,
          id: t.id || t.ticket_code,
          code: t.code || t.ticket_code,
          sp: Number(t.sp !== undefined ? t.sp : (t.story_point || 0)),
          assignee: (t.assignee && t.assignee.length > 0) ? (typeof t.assignee === 'string' ? t.assignee : t.assignee[0].name) : '',
          assignees: Array.isArray(t.assignee) ? t.assignee : (t.assignees || []),
          sprintId: null
        })));
      }

      setTickets(prev => {
        if (currentPage > 1) {
          const actualSprintId = apiMyTickets.active_sprint?.id || activeSprint?.id || null;
          const newMyTickets = newTickets.filter(t => t.sprintId === actualSprintId);
          return [...prev, ...newMyTickets];
        }
        return newTickets;
      });
      const hasMoreMyTickets = (apiMyTickets.tickets?.length === 15) || 
                               (apiMyTickets.backlog?.tickets?.length === 15);
      setHasMoreTickets(hasMoreMyTickets);
      
      // Update ref to indicate sync is complete
      lastSyncedMyTickets.current = apiMyTickets;
    }
  }, [apiMyTickets, activeSprint?.id, currentPage, selectedCardType]);

  // Optimistic deltas for UI counts during drag-and-drop background syncs
  const [optimisticDeltas, setOptimisticDeltas] = useState({
    totalBacklog: 0,
    myTicketsBacklog: 0,
    myTicketsTotal: 0
  });

  useEffect(() => {
    setOptimisticDeltas({ totalBacklog: 0, myTicketsBacklog: 0, myTicketsTotal: 0 });
  }, [dashboard, sprintsTickets, apiMyTickets]);

  // Filters & Sorting state
  const [priorityFilter] = useState('ALL'); // 'ALL', 'P1', 'P2', 'P3'
  const [selectedTeamMember] = useState('ALL'); 

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // View switch: expanded view for fullscreen tables
  const [isExpandedView, setIsExpandedView] = useState(false);

  // Modal open states
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [sprintMenuAnchor, setSprintMenuAnchor] = useState(null);
  const [sprintDropdownOptions, setSprintDropdownOptions] = useState([]);
  const [sprintDropdownLoading, setSprintDropdownLoading] = useState(false);

  useEffect(() => {
    if (sprintMenuAnchor && selectedProjectId) {
      setSprintDropdownLoading(true);
      api.get(`/tickets/dropdowns?project_id=${selectedProjectId}&type=sprint`)
        .then(res => {
          if (res.data?.status) {
            setSprintDropdownOptions(res.data.data || []);
          }
        })
        .catch(err => console.error(err))
        .finally(() => setSprintDropdownLoading(false));
    }
  }, [sprintMenuAnchor, selectedProjectId]);
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [sprintModalData, setSprintModalData] = useState(null);

  // Complete Sprint Dialog state
  const [isCompleteSprintDialogOpen, setIsCompleteSprintDialogOpen] = useState(false);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [completeSprintDialogType, setCompleteSprintDialogType] = useState('warning');
  const [isCompletingSprint, setIsCompletingSprint] = useState(false);

  // Blocker error modal
  const [blockerTitle, setBlockerTitle] = useState('backlog.capacityBlockerTitle');
  const [blockerMessage, setBlockerMessage] = useState('');
  const [isBlockerOpen, setIsBlockerOpen] = useState(false);

  // Track if local state (tickets) is currently syncing with Redux state
  const lastSyncedSprintsTickets = useRef(null);
  const lastSyncedMyTickets = useRef(null);

  const isMyTicketsDataView = selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS && currentMainView !== 'backlog';

  const isSyncingTickets = isMyTicketsDataView
    ? (apiMyTickets !== lastSyncedMyTickets.current)
    : (sprintsTickets !== lastSyncedSprintsTickets.current);

  // Pagination / Scroll limits
  const isDraftWithoutSprintId = !isMyTicketsDataView && getSprintStateFromCardType(selectedCardType) === 'DRAFT' && !(dashboard?.upcoming_sprint?.id || dashboard?.planned_sprint?.id);

  const isFetchingData = dashboardLoading || 
    scrumRole === null ||
    (isMyTicketsDataView ? myTicketsLoading : sprintsTicketsLoading) || 
    (!sprintsTickets && !isMyTicketsDataView && !isDraftWithoutSprintId) ||
    (!apiMyTickets && isMyTicketsDataView);

  const isLoadingMore = currentPage > 1 && isFetchingData;
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!isFetchingData) {
      loadingMoreRef.current = false;
      isCoreViewChange.current = false;
    }
  }, [isFetchingData]);

  const isBackgroundSyncActive = useRef(false);

  useEffect(() => {
    if (!isSyncingTickets) {
      isBackgroundSyncActive.current = false;
    }
  }, [isSyncingTickets]);

  const isTableLoading = currentPage === 1 && isCoreViewChange.current && (isFetchingData || (isSyncingTickets && !isBackgroundSyncActive.current));

  // List of active sprint tickets
  const activeSprintTickets = useMemo(() => tickets.filter(t => t.sprintId === activeSprint?.id), [tickets, activeSprint]);

  // Calculate stats for Active Sprint Card
  const activeSprintStats = useMemo(() => ({
    completed: activeSprintTickets.filter(t => t.status === 'Done').reduce((sum, t) => sum + t.sp, 0),
    p1Pending: activeSprintTickets.filter(t => ((typeof t.priority === 'object' ? t.priority.name : t.priority) || '').toLowerCase() === 'critical' && t.status !== 'Done').length,
    total: activeSprintTickets.reduce((sum, t) => sum + t.sp, 0)
  }), [activeSprintTickets]);

  const activeSprintProgress = useMemo(() => {
    if (dashboard?.active_sprint?.completed_percentage !== undefined) return dashboard.active_sprint.completed_percentage;
    if (activeSprintStats.total === 0) return 0;
    return Math.round((activeSprintStats.completed / activeSprintStats.total) * 100);
  }, [dashboard, activeSprintStats]);


  // Dynamic metrics for the cards
  const upcomingPlannedSprint = useMemo(() => {
    return sprints.find(s => s.status === SPRINT_STATUSES.PLANNED) || sprints[1];
  }, [sprints]);

  const upcomingPlannedSprintCount = useMemo(() => {
    if (dashboard?.planned_sprint?.count !== undefined) return dashboard.planned_sprint.count;
    return sprints.filter(s => s.status === SPRINT_STATUSES.PLANNED).length;
  }, [dashboard, sprints]);

  const upcomingPlannedSprintDates = useMemo(() => {
    if (dashboard?.planned_sprint?.dates) return dashboard.planned_sprint.dates;
    return upcomingPlannedSprint ? formatDateRange(upcomingPlannedSprint.startDate, upcomingPlannedSprint.endDate, true) : 'TBD';
  }, [dashboard, upcomingPlannedSprint]);

  const activeSprintP1Pending = useMemo(() => {
    if (dashboard?.active_sprint?.pending_p1 !== undefined) return dashboard.active_sprint.pending_p1;
    return activeSprintStats.p1Pending;
  }, [dashboard, activeSprintStats]);

  const activeSprintDaysRemaining = useMemo(() => {
    if (dashboard?.active_sprint?.sprint_remaining_days !== undefined) return dashboard.active_sprint.sprint_remaining_days;
    if (!activeSprint || !activeSprint.endDate) return 0;
    const end = new Date(activeSprint.endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }, [dashboard, activeSprint]);

  const activeSprintTiming = useMemo(() => {
    if (dashboard?.active_sprint?.timing) return dashboard.active_sprint.timing;
    return formatDateRange(activeSprint?.startDate, activeSprint?.endDate);
  }, [dashboard, activeSprint]);

  const activeSprintTicketCount = useMemo(() => {
    if (dashboard?.active_sprint?.count !== undefined) return dashboard.active_sprint.count;
    return activeSprintTickets.length;
  }, [dashboard, activeSprintTickets]);

  const myTickets = useMemo(() => {
    return tickets.filter(t => t.assignee === currentUserInitials && (t.sprintId === activeSprint?.id || t.sprintId === null));
  }, [tickets, activeSprint, currentUserInitials]);

  const myTicketsCount = useMemo(() => {
    if (dashboard?.my_tickets?.count !== undefined) return dashboard.my_tickets.count + optimisticDeltas.myTicketsTotal;
    return myTickets.length + optimisticDeltas.myTicketsTotal;
  }, [dashboard, myTickets, optimisticDeltas]);

  const myHighPriorityCount = useMemo(() => {
    if (dashboard?.my_tickets?.high_priority !== undefined) return dashboard.my_tickets.high_priority;
    return myTickets.filter(t => ((typeof t.priority === 'object' ? t.priority.name : t.priority) || '').toLowerCase() === 'critical').length;
  }, [dashboard, myTickets]);

  const myBugsCount = useMemo(() => {
    if (dashboard?.my_tickets?.bugs?.count !== undefined) return dashboard.my_tickets.bugs.count;
    // local fallback
    return tickets.filter(t => t.assignee === currentUserInitials && t.type === 'Bug').length;
  }, [dashboard, tickets, currentUserInitials]);

  // Filtered Main Sprint tickets
  const filteredMainSprintTickets = useMemo(() => {
    if (!currentMainSprint) return [];
    let result = tickets.filter(t => t.sprintId === currentMainSprint.id);
    
    // Search filter
    if (searchTerm.trim() !== '') {
      result = result.filter(t => 
        (t.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (t.code || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Priority filter
    if (priorityFilter !== 'ALL') {
      result = result.filter(t => (t.priority?.name || t.priority) === priorityFilter);
    }


    return result;
  }, [tickets, currentMainSprint, searchTerm, priorityFilter]);

  // Filtered Unassigned Backlog tickets
  const filteredBacklogTickets = useMemo(() => {
    let result = tickets.filter(t => t.sprintId === null);

    if (searchTerm.trim() !== '') {
      result = result.filter(t => 
        (t.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (t.code || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (priorityFilter !== 'ALL') {
      result = result.filter(t => (t.priority?.name || t.priority) === priorityFilter);
    }


    return result;
  }, [tickets, searchTerm, priorityFilter]);

  const getFilteredTicketsForScope = (scopeId) => {
    let list = [];
    if (scopeId === 'backlog') {
      list = tickets.filter(t => t.sprintId === null);
    } else {
      list = tickets.filter(t => t.sprintId === scopeId);
    }

    if (selectedTeamMember !== 'ALL') {
      list = list.filter(t => t.assignee === selectedTeamMember);
    }
    return list;
  };

  // Infinite Scroll Trigger
  const handleScroll = (e, type) => {
    const target = e.target;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 10) {
      if (loadingMoreRef.current || !hasMoreTickets || isFetchingData) return;
      loadingMoreRef.current = true;
      setCurrentPage(prev => prev + 1);
    }
  };

  // Drag and Drop implementation
  // Drag and Drop implementation
  const [draggedTickets, setDraggedTickets] = useState([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [isDragOverSprint, setIsDragOverSprint] = useState(false);

  const handleSelectTicket = (ticketId) => {
    setSelectedTicketIds(prev => {
      if (prev.includes(ticketId)) {
        return prev.filter(id => id !== ticketId);
      } else {
        return [...prev, ticketId];
      }
    });
  };

  const handleSelectAllVisible = (ticketsList) => {
    const visibleIds = ticketsList.map(t => t.id);
    const allSelected = visibleIds.every(id => selectedTicketIds.includes(id));
    if (allSelected) {
      setSelectedTicketIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedTicketIds(prev => {
        const newIds = [...prev];
        visibleIds.forEach(id => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        return newIds;
      });
    }
  };

  const handleDragStart = (e, ticket) => {
    if (!isAuthorized) return;
    if (!ticket?.id) {
      console.error('Invalid ticket for drag', ticket);
      return;
    }
    
    let dragging = [ticket];
    if (selectedTicketIds.includes(ticket.id)) {
      dragging = tickets.filter(t => selectedTicketIds.includes(t.id));
    }
    setDraggedTickets(dragging);

    if (dragging.length > 1 && e && e.dataTransfer) {
      const dragBadge = document.createElement('div');
      dragBadge.id = 'drag-ghost';
      dragBadge.style.position = 'absolute';
      dragBadge.style.top = '-1000px';
      dragBadge.style.left = '-1000px';
      dragBadge.style.background = '#4f46e5'; // Brand Primary
      dragBadge.style.color = 'white';
      dragBadge.style.padding = '6px 12px';
      dragBadge.style.borderRadius = '20px';
      dragBadge.style.fontWeight = 'bold';
      dragBadge.style.fontSize = '14px';
      dragBadge.style.zIndex = '9999';
      dragBadge.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
      dragBadge.innerText = `Moving ${dragging.length} tickets`;
      document.body.appendChild(dragBadge);
      
      e.dataTransfer.setDragImage(dragBadge, 15, 15);

      // Cleanup DOM after drag starts, using setTimeout to ensure the browser has time to capture the image
      setTimeout(() => {
        if (document.body.contains(dragBadge)) {
          document.body.removeChild(dragBadge);
        }
      }, 0);
    }
  };

  const handleDragOver = (e) => {
    if (!isAuthorized) return;
    e.preventDefault();
    setIsDragOverSprint(true);
  };

  const handleDragLeave = () => {
    setIsDragOverSprint(false);
  };

  const handleDropOnTarget = (e, targetId) => {
    if (!isAuthorized || draggedTickets.length === 0) return;
    e.preventDefault();
    setIsDragOverSprint(false);

    const destinationSprintId = targetId === 'backlog' ? null : targetId;
    const actualDraggedTickets = draggedTickets.filter(t => t.sprintId !== destinationSprintId);

    if (actualDraggedTickets.length === 0) {
      setDraggedTickets([]);
      setSelectedTicketIds([]);
      return;
    }

    const draggedIds = actualDraggedTickets.map(t => t.id);

    if (targetId === 'backlog') {
      // Move tickets back to backlog
      const updatedTickets = tickets.map(t => {
        if (draggedIds.includes(t.id)) {
          return { ...t, sprintId: null };
        }
        return t;
      });
      
      // Optimistic updates for counts and sprints
      const draggedPoints = actualDraggedTickets.reduce((sum, t) => sum + Number(t.sp || 0), 0);
      const draggedP1s = actualDraggedTickets.filter(t => ((typeof t.priority === 'object' ? t.priority.name : t.priority) || '').toLowerCase() === 'critical' && t.status !== 'Done').length;
      
      const draggedMyTicketsCount = actualDraggedTickets.filter(t => {
        if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) return true;
        if (t.assignee === currentUserInitials) return true;
        if (t.assignees && Array.isArray(t.assignees)) {
          return t.assignees.some(a => 
            a === currentUserInitials || 
            a?.initials === currentUserInitials || 
            a?.name === currentUserInitials ||
            a?.id === currentUserInitials
          );
        }
        return false;
      }).length;

      const draggedCount = actualDraggedTickets.length;
      const sourceId = actualDraggedTickets[0].sprintId;

      setSprints(prevSprints => prevSprints.map(s => {
        let newS = { ...s };
        if (s.id === sourceId) {
          newS.totalPoints = Math.max(0, (newS.totalPoints || 0) - draggedPoints);
          newS.pendingP1Count = Math.max(0, (newS.pendingP1Count || 0) - draggedP1s);
          if (newS.count !== undefined) newS.count = Math.max(0, newS.count - draggedCount);
        }
        return newS;
      }));

      setOptimisticDeltas(prev => {
        let newD = { ...prev };
        newD.totalBacklog += draggedCount;
        newD.myTicketsBacklog += draggedMyTicketsCount;
        newD.myTicketsTotal -= draggedMyTicketsCount;
        return newD;
      });

      // API Call for each ticket
      Promise.all(draggedIds.map(ticketId => 
        dispatch(updateTicketPartial({
          ticketId,
          ticketData: { scrum_sprint_id: null }
        })).unwrap().catch(err => ({ error: true, id: ticketId, message: err.message }))
      )).then((results) => {
        const failed = results.filter(r => r?.error);
        if (failed.length === 0) {
          toast.success('Tickets moved to Backlog');
        } else if (failed.length === draggedIds.length) {
          toast.error(`Failed to move ${failed.length} tickets. Please try again.`);
        } else {
          toast.warning(`Moved ${draggedIds.length - failed.length} tickets. ${failed.length} failed.`);
        }
        
        if (failed.length > 0) {
          const failedIds = failed.map(f => f.id);
          setSelectedTicketIds(prevIds => [...new Set([...prevIds, ...failedIds])]);
          // Revert UI state for failed tickets
          setTickets(prev => prev.map(t => {
            if (failedIds.includes(t.id)) {
              const originalTicket = tickets.find(orig => orig.id === t.id);
              return { ...t, sprintId: originalTicket?.sprintId };
            }
            return t;
          }));
        }

        // Sync with backend to update counts (e.g., totalBacklogCount, My Tickets count)
        if (selectedProjectId) {
          isBackgroundSyncActive.current = true;
          dispatch(fetchScrumDashboard({ projectId: selectedProjectId, background: true }));
          
          let sortParam = sortOption === 'PRIORITY_HIGH_TO_LOW' ? 'priority' : (sortOption === 'SP_LOW_TO_HIGH' ? 'story' : '');
          let state = getSprintStateFromCardType(selectedCardType);
          let sprintId = null;

          if (currentMainView === 'backlog') {
            state = 'BACKLOG';
          } else if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
            state = 'my_tickets';
          } else if (state === 'DRAFT') {
            sprintId = dashboard?.upcoming_sprint?.id || dashboard?.planned_sprint?.id;
          }

          if (state === 'my_tickets') {
            dispatch(fetchMyScrumTickets({ projectId: selectedProjectId, page: currentPage, limit: 15, search: debouncedSearchTerm, sort: sortParam, background: true }));
          } else {
            dispatch(fetchSprintsTickets({ projectId: selectedProjectId, state, sprintId, page: currentPage, limit: 15, search: debouncedSearchTerm, sort: sortParam, background: true }));
          }
        }
      }).catch(err => {
        toast.error('An unexpected error occurred while moving tickets.');
        console.error('Promise.all failed:', err);
      });

      saveTickets(updatedTickets);
      setDraggedTickets([]);
      setSelectedTicketIds([]);
    } else {
      // Move tickets to a sprint
      const targetSprint = sprints.find(s => s.id === targetId);
      if (!targetSprint) return;

      // Check if any dragged ticket lacks story points
      const ticketsWithoutSp = actualDraggedTickets.filter(t => !t.sp || t.sp <= 0);
      if (ticketsWithoutSp.length > 0) {
        setBlockerTitle('backlog.storyPointsMissingTitle');
        setBlockerMessage('Please add story points before assigning this ticket to a sprint. Once story points are added, you\'ll be able to move it into the sprint.');
        setIsBlockerOpen(true);
        return;
      }

      // Check capacity constraints
      const capacityLimit = Number(targetSprint?.capacity || 0);
      const pointsOfDraggedTicketsAlreadyInTarget = actualDraggedTickets
        .filter(t => t.sprintId === targetId)
        .reduce((sum, t) => sum + Number(t.sp || 0), 0);
        
      const currentSprintPoints = (targetSprint?.totalPoints !== undefined ? targetSprint.totalPoints : tickets
        .filter(t => t.sprintId === targetId)
        .reduce((sum, t) => sum + Number(t.sp || 0), 0)) - pointsOfDraggedTicketsAlreadyInTarget;
      
      const draggedPoints = actualDraggedTickets.reduce((sum, t) => sum + Number(t.sp || 0), 0);
      const newTotalPoints = currentSprintPoints + draggedPoints;
      
      if (!capacityLimit || capacityLimit <= 0) {
        setBlockerTitle('backlog.capacityBlockerTitle');
        setBlockerMessage('Sprint capacity not configured');
        setIsBlockerOpen(true);
        return;
      }

      if (newTotalPoints > capacityLimit) {
        setBlockerTitle('backlog.capacityBlockerTitle');
        setBlockerMessage(isScrumMaster
          ? 'backlog.capacityReachedMessage'
          : 'backlog.capacityReachedContactScrumMasterMessage');
        setIsBlockerOpen(true);
        return;
      }

      // Update tickets' sprint assignment
      const updatedTickets = tickets.map(t => {
        if (draggedIds.includes(t.id)) {
          return { ...t, sprintId: targetId };
        }
        return t;
      });

      // Optimistic updates for counts and sprints
      const draggedP1s = actualDraggedTickets.filter(t => ((typeof t.priority === 'object' ? t.priority.name : t.priority) || '').toLowerCase() === 'critical' && t.status !== 'Done').length;
      
      const draggedMyTicketsCount = actualDraggedTickets.filter(t => {
        if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) return true;
        if (t.assignee === currentUserInitials) return true;
        if (t.assignees && Array.isArray(t.assignees)) {
          return t.assignees.some(a => 
            a === currentUserInitials || 
            a?.initials === currentUserInitials || 
            a?.name === currentUserInitials ||
            a?.id === currentUserInitials
          );
        }
        return false;
      }).length;

      const draggedCount = actualDraggedTickets.length;
      const sourceId = actualDraggedTickets[0].sprintId;

      setSprints(prevSprints => prevSprints.map(s => {
        let newS = { ...s };
        if (s.id === sourceId) {
          newS.totalPoints = Math.max(0, (newS.totalPoints || 0) - draggedPoints);
          newS.pendingP1Count = Math.max(0, (newS.pendingP1Count || 0) - draggedP1s);
          if (newS.count !== undefined) newS.count = Math.max(0, newS.count - draggedCount);
        }
        if (s.id === targetId) {
          newS.totalPoints = (newS.totalPoints || 0) + draggedPoints;
          newS.pendingP1Count = (newS.pendingP1Count || 0) + draggedP1s;
          if (newS.count !== undefined) newS.count = newS.count + draggedCount;
        }
        return newS;
      }));

      setOptimisticDeltas(prev => {
        let newD = { ...prev };
        if (targetId === null) {
          newD.totalBacklog += draggedCount;
          newD.myTicketsBacklog += draggedMyTicketsCount;
          newD.myTicketsTotal -= draggedMyTicketsCount;
        }
        if (sourceId === null) {
          newD.totalBacklog -= draggedCount;
          newD.myTicketsBacklog -= draggedMyTicketsCount;
          newD.myTicketsTotal += draggedMyTicketsCount;
        }
        return newD;
      });

      // API Call for each ticket
      Promise.all(draggedIds.map(ticketId => 
        dispatch(updateTicketPartial({
          ticketId,
          ticketData: { scrum_sprint_id: targetId }
        })).unwrap().catch(err => ({ error: true, id: ticketId, message: err.message }))
      )).then((results) => {
        const failed = results.filter(r => r?.error);
        if (failed.length === 0) {
          toast.success(`Tickets moved to ${targetSprint.name}`);
        } else if (failed.length === draggedIds.length) {
          toast.error(`Failed to move ${failed.length} tickets. Please try again.`);
        } else {
          toast.warning(`Moved ${draggedIds.length - failed.length} tickets. ${failed.length} failed.`);
        }
        
        if (failed.length > 0) {
          const failedIds = failed.map(f => f.id);
          setSelectedTicketIds(prevIds => [...new Set([...prevIds, ...failedIds])]);
          // Revert UI state for failed tickets
          setTickets(prev => prev.map(t => {
            if (failedIds.includes(t.id)) {
              const originalTicket = tickets.find(orig => orig.id === t.id);
              return { ...t, sprintId: originalTicket?.sprintId };
            }
            return t;
          }));
        }

        // Sync with backend to update counts (e.g., totalBacklogCount, My Tickets count)
        if (selectedProjectId) {
          isBackgroundSyncActive.current = true;
          dispatch(fetchScrumDashboard({ projectId: selectedProjectId, background: true }));
          
          let sortParam = sortOption === 'PRIORITY_HIGH_TO_LOW' ? 'priority' : (sortOption === 'SP_LOW_TO_HIGH' ? 'story' : '');
          let state = getSprintStateFromCardType(selectedCardType);
          let sprintId = null;

          if (currentMainView === 'backlog') {
            state = 'BACKLOG';
          } else if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
            state = 'my_tickets';
          } else if (state === 'DRAFT') {
            sprintId = dashboard?.upcoming_sprint?.id || dashboard?.planned_sprint?.id;
          }

          if (state === 'my_tickets') {
            dispatch(fetchMyScrumTickets({ projectId: selectedProjectId, page: currentPage, limit: 15, search: debouncedSearchTerm, sort: sortParam, background: true }));
          } else {
            dispatch(fetchSprintsTickets({ projectId: selectedProjectId, state, sprintId, page: currentPage, limit: 15, search: debouncedSearchTerm, sort: sortParam, background: true }));
          }
        }
      }).catch(err => {
        toast.error('An unexpected error occurred while moving tickets.');
        console.error('Promise.all failed:', err);
      });

      saveTickets(updatedTickets);
      setDraggedTickets([]);
      setSelectedTicketIds([]);
    }
  };


  const completeSprintAction = async () => {
    if (isCompletingSprint) return;

    setIsCompletingSprint(true);

    const updatedTickets = tickets.map(t => {
      if (t.sprintId === activeSprint?.id && t.status !== 'Done') {
        return { ...t, sprintId: null };
      }
      return t;
    });

    const updatedSprints = sprints.filter(s => s.id !== activeSprint?.id);

    try {
      // API Call
      await dispatch(updateSprintStatus({ sprintId: activeSprint?.id, data: { status: 'COMPLETE' } })).unwrap();

      saveTickets(updatedTickets);
      saveSprints(updatedSprints);
      setCurrentMainView(null);
      setIsCompleteSprintDialogOpen(false);
      
      // Refetch data
      dispatch(fetchScrumDashboard(selectedProjectId));
      if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
        dispatch(fetchMyScrumTickets({ projectId: selectedProjectId, page: 1, limit: 15, search: debouncedSearchTerm }));
      } else {
        let state = getSprintStateFromCardType(selectedCardType);
        let sprintId = null;
        if (state === 'DRAFT') {
          sprintId = dashboard?.upcoming_sprint?.id || dashboard?.planned_sprint?.id;
        }
        dispatch(fetchSprintsTickets({ projectId: selectedProjectId, state, sprintId, page: 1, limit: 15, search: debouncedSearchTerm }));
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to complete sprint. Please try again.');
    } finally {
      setIsCompletingSprint(false);
    }
  };

  // Create task modal integration
  const handleCreateTicketSubmit = async (taskData) => {
    try {
      const createdTask = await dispatch(createTicket(taskData)).unwrap();

      const resolvedTaskType = dropdowns?.types?.find(type => String(type.id) === String(taskData.taskType)) || null;
      const resolvedPriority = dropdowns?.priorities?.find(priority => String(priority.id) === String(taskData.priority)) || null;
      const resolvedStatus = dropdowns?.statuses?.find(status => String(status.id) === String(taskData.status)) || null;
      const resolvedAssignees = (taskData.assignees || [])
        .map(assigneeId => dropdowns?.assignees?.find(a => String(a.id) === String(assigneeId)))
        .filter(Boolean);

      const newTicket = {
        id: createdTask?.id || `TK-${Date.now()}`,
        code: createdTask?.ticket_code || createdTask?.ticket_number || `PMPI-${tickets.length + 101}`,
        title: createdTask?.title || taskData.title,
        type: createdTask?.task_type?.name || createdTask?.type?.name || resolvedTaskType?.name || dropdowns?.types?.find(t => t.name === 'Task')?.name || dropdowns?.types?.[0]?.name || 'Task',
        priority: createdTask?.priority?.name || resolvedPriority?.name || getUnassignedPriority(dropdowns?.priorities)?.name || '',
        sp: createdTask?.story_points || taskData.story_points || taskData.storyPoints || null,
        assignee: createdTask?.assignee?.name || resolvedAssignees[0]?.name || currentUserInitials,
        status: createdTask?.status?.name || resolvedStatus?.name || dropdowns?.statuses?.find(s => s.name === 'To Do')?.name || dropdowns?.statuses?.[0]?.name || 'To Do',
        sprintId: taskData.sprint || (currentMainView === 'backlog' ? null : currentMainSprint?.id)
      };

      saveTickets([newTicket, ...tickets]);
      setIsAddTaskOpen(false);

      // Refresh data via API silently in the background
      if (selectedProjectId) {
        isBackgroundSyncActive.current = true;
        dispatch(fetchTicketDropdowns(selectedProjectId));
        dispatch(fetchScrumDashboard({ projectId: selectedProjectId, background: true }));
        
        if (currentMainView === 'my_tickets') {
          dispatch(fetchMyScrumTickets({ projectId: selectedProjectId, page: currentPage, limit: 15, search: debouncedSearchTerm, background: true }));
        } else {
          let state = getSprintStateFromCardType(selectedCardType);
          let sprintId = null;
          if (currentMainView === 'backlog') {
            state = 'BACKLOG';
          } else if (state === 'DRAFT') {
            sprintId = dashboard?.upcoming_sprint?.id || dashboard?.planned_sprint?.id;
          }
          dispatch(fetchSprintsTickets({ projectId: selectedProjectId, state, sprintId, page: currentPage, limit: 15, search: debouncedSearchTerm, background: true }));
        }
      }
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };





  const handleSprintStatusClick = () => {
    if (!isAuthorized || !currentMainSprint) return;
    if (currentMainSprint.status === SPRINT_STATUSES.ACTIVE) {
      const incompleteCountApi = currentMainSprint.incompleteTicketCount;
      const incompleteLocal = activeSprintTickets.filter(t => t.status !== 'Done').length;
      const finalIncompleteCount = incompleteCountApi !== undefined ? incompleteCountApi : incompleteLocal;
      
      if (finalIncompleteCount > 0) {
        setIncompleteCount(finalIncompleteCount);
        setCompleteSprintDialogType('warning');
        setIsCompleteSprintDialogOpen(true);
      } else {
        setIncompleteCount(0);
        setCompleteSprintDialogType('confirmation');
        setIsCompleteSprintDialogOpen(true);
      }
    } else if (currentMainSprint.status === SPRINT_STATUSES.PLANNED) {
      // API Call
      dispatch(updateSprintStatus({ sprintId: currentMainSprint.id, data: { status: 'ACTIVE' } }));

      const updatedSprints = sprints.map(s => {
        if (s.id === currentMainSprint.id) return { ...s, status: 'active' };
        return s;
      });
      saveSprints(updatedSprints);
    }
  };

  const handleSortClick = (e) => {
    setSortAnchorEl(e.currentTarget);
  };
  const handleSortClose = () => {
    setSortAnchorEl(null);
  };
  const handleSortSelect = (option) => {
    setSortOption(option);
    handleSortClose();
  };

  const getSprintCheckboxProps = () => {
    if (selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS) {
      return { hidden: true, checked: false, disabled: true, tooltip: '', onChange: () => {} };
    }
    if (!currentMainSprint) return { checked: false, disabled: true, tooltip: '', onChange: () => {} };
    if (currentMainSprint.status === 'completed') {
      return {
        checked: true,
        disabled: true,
        tooltip: "Sprint Completed",
        onChange: () => {}
      };
    }
    if (currentMainSprint.status === SPRINT_STATUSES.ACTIVE) {
      return {
        checked: false,
        disabled: false,
        tooltip: "Mark sprint as complete",
        onChange: handleSprintStatusClick
      };
    }
    // planned
    return {
      hidden: true,
      checked: false,
      disabled: true,
      tooltip: "",
      onChange: () => {}
    };
  };


  const checkboxProps = getSprintCheckboxProps();

  const visibleBacklogTickets = useMemo(() => {
    return filteredBacklogTickets;
  }, [filteredBacklogTickets]);

  const visibleSprintTickets = useMemo(() => {
    return filteredMainSprintTickets;
  }, [filteredMainSprintTickets]);

  // Capacity Warning logic
  const CAPACITY_WARNING_THRESHOLD = 0.85;
  const currentTotalPoints = Number(currentMainSprint?.totalPoints || 0);
  const currentCapacity = Number(currentMainSprint?.capacity || 0);
  const sprintUtilization = currentCapacity > 0 ? currentTotalPoints / currentCapacity : 0;
  const capacityWarningUsedPoints = Math.max(0, Math.round(currentTotalPoints));
  const capacityWarningTotalPoints = Math.max(0, Math.round(currentCapacity));
  const showCapacityWarning = selectedCardType === BACKLOG_CARD_TYPES.ACTIVE_SPRINT
    && !isTableLoading
    && currentMainSprint
    && currentCapacity > 0
    && sprintUtilization >= CAPACITY_WARNING_THRESHOLD;

  return (
    <div className="dashboard">
      <Navbar />

      {audioPromptReady && showAudioPrompt && (
        <Box className="audio-permission-banner">
          <img src={bellIcon} alt="" className="audio-permission-banner-icon" />
          <Typography className="audio-permission-banner-text">
            {t('dashboard.enableNotificationSounds', 'Click anywhere to enable notification sounds')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowAudioPrompt(false)}
            className="audio-permission-banner-close"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      <main className="main backlog-main-container">
        {/* Page Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          sx={{ px: 2, py: 1, flexShrink: 0, borderRadius: '8px' }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" fontWeight={600} color={COLORS.text.muted} sx={{ fontSize: '1.1rem' }}>
              {t('backlog.title')}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1.5}>
            <Select
              value={selectedProjectId || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedProjectId(value);
                if (value && String(value).toUpperCase() !== 'ALL') {
                  dispatch(saveUserPreference({
                    preferenceKey: 'last_selected_project',
                    preferenceValue: { project_id: value }
                  }));
                  dispatch(setFilters({ selectedProject: value }));
                } else {
                  dispatch(setFilters({ selectedProject: 'ALL' }));
                }
              }}
              displayEmpty
              size="small"
              sx={{ 
                minWidth: 200, 
                maxWidth: 200,
                height: 36,
                bgcolor: COLORS.white,
                '& .MuiSelect-select': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.slate[300] },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.slate[400] },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.blue[600] }
              }}
            >
              <MenuItem value="" disabled>{t('backlog.selectProject')}</MenuItem>
              {projectDropdownsByUser && projectDropdownsByUser
                .filter(project => (project.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase())
                .map(project => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title="Refresh data" arrow>
              <IconButton 
                onClick={handleRefresh}
                sx={{ 
                  bgcolor: COLORS.white, 
                  border: '1px solid #cbd5e1', 
                  borderRadius: '6px',
                  p: 1,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  '&:hover': {
                    bgcolor: COLORS.slate[50]
                  }
                }}
              >
                <Refresh sx={{ fontSize: 20, color: '#475569' }} />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('dashboard.notifications')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
              <IconButton 
                onClick={() => navigate('/notifications', { state: { from: location.pathname } })}
                className={`dashboard-header-icon-button ${bellRinging ? 'bell-ringing' : ''}`}
                aria-label={t('dashboard.notifications')}
                sx={{ 
                  bgcolor: COLORS.white, 
                  border: '1px solid #cbd5e1', 
                  borderRadius: '6px',
                  p: 1,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                <Badge 
                  badgeContent={notificationBadgeLabel} 
                  invisible={!notificationBadgeLabel}
                  className="dashboard-notification-badge"
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <img
                    src={bellIcon}
                    alt={t('dashboard.notifications')}
                    style={{ width: 20, height: 20 }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>

            <HeaderProfileMenu />
          {isAuthorized && isScrumMaster && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title={dashboardLoading || projectDropdownsByUserLoading ? t('dashboard.loading', 'Loading...') : ''} arrow placement="bottom" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<Edit sx={{ fontSize: 16 }} />}
                    onClick={(e) => { setSprintMenuAnchor(e.currentTarget); }}
                    disabled={dashboardLoading || projectDropdownsByUserLoading}
                    className="edit-sprint-btn"
                  >
                    Edit Sprint
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title={dashboardLoading || projectDropdownsByUserLoading ? t('dashboard.loading', 'Loading...') : ''} arrow placement="bottom" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => { setSprintModalData(null); setShowSprintModal(true); }}
                    disabled={dashboardLoading || projectDropdownsByUserLoading}
                    className="create-sprint-btn"
                  >
                    {t('dashboard.createSprint', 'Create Sprint')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

        {/* View Switch Navigation */}
        <div className="backlog-workspace">
          <div className="backlog-navigation-bar">
            <div className="backlog-tab-button active">
              Backlog
            </div>
            <div className="backlog-tab-button disabled">
              Roadmap
            </div>
          </div>

          {/* Main workspace splits */}
          {((!selectedProjectId || String(selectedProjectId).toUpperCase() === 'ALL') && !projectDropdownsByUserLoading) ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 8, 
              flex: 1, 
              minHeight: '400px',
              bgcolor: '#f8fafc',
              borderRadius: '8px',
              m: 3,
              border: '1px dashed #cbd5e1'
            }}>
              {projectDropdownsByUser && projectDropdownsByUser.filter(p => (p.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase()).length > 0 ? (
                <>
                  <Typography variant="h5" color={COLORS.slate[700]} fontWeight={600} gutterBottom>
                    No Scrum Project Selected
                  </Typography>
                  <Typography variant="body1" color={COLORS.slate[500]} textAlign="center" maxWidth={400}>
                    Please select an active Scrum project from the dropdown above to view its backlog, active sprints, and tickets.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h5" color={COLORS.slate[700]} fontWeight={600} gutterBottom>
                    No Scrum Projects Available
                  </Typography>
                  <Typography variant="body1" color={COLORS.slate[500]} textAlign="center" maxWidth={400}>
                    You do not currently have any active Scrum projects. Please create a new Scrum project to use the Backlog and Sprint planning features.
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <div className={`backlog-main-grid ${isExpandedView ? 'fullscreen-layout' : ''}`}>
              
              {/* Left pane wrapper */}
            <div className={`backlog-left-pane ${isExpandedView ? 'fullscreen-layout' : ''}`}>
              
              {/* Cards Grid */}
              {/* Cards Grid Component */}
              <Box display="flex" flexDirection="column">
                <BacklogMetricCards 
                  isLoading={projectDropdownsByUserLoading || !selectedProjectId || dashboardLoading || !dashboard}
                  isExpandedView={isExpandedView}
                  selectedCardType={selectedCardType}
                  setSelectedCardType={handleSetSelectedCardType}
                  activeSprint={activeSprint}
                  setCurrentMainView={handleSetCurrentMainView}
                  currentMainSprintId={currentMainSprint?.id}
                  formatDateRange={formatDateRange}
                  activeSprintP1Pending={activeSprintP1Pending}
                  activeSprintDaysRemaining={activeSprintDaysRemaining}
                  activeSprintProgress={activeSprintProgress}
                  upcomingPlannedSprint={upcomingPlannedSprint}
                  upcomingPlannedSprintCount={upcomingPlannedSprintCount}
                  upcomingPlannedSprintDates={upcomingPlannedSprintDates}
                  activeSprintTiming={activeSprintTiming}
                  activeSprintTicketCount={activeSprintTicketCount}
                  myHighPriorityCount={myHighPriorityCount}
                  myBugsCount={myBugsCount}
                  myTicketsCount={myTicketsCount}
                />

                {/* Warning Capacity Banner */}
                <Collapse in={showCapacityWarning}>
                  <div className="capacity-warning-banner" style={{ marginTop: '16px', marginBottom: 0 }}>
                    <WarningAmber sx={{ fontSize: 18 }} />
                    {t('backlog.capacityWarning', {
                      used: capacityWarningUsedPoints,
                      total: capacityWarningTotalPoints
                    })}
                  </div>
                </Collapse>
              </Box>

              {/* Left pane: Dynamic Active view table */}
              <BacklogTable 
                currentMainView={currentMainView}
                selectedCardType={selectedCardType}
                isMyTicketsDataView={isMyTicketsDataView}
                currentMainSprint={currentMainSprint}
                isDragOverSprint={isDragOverSprint}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDropOnTarget={handleDropOnTarget}
                setIsAddTaskOpen={setIsAddTaskOpen}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                handleSortClick={handleSortClick}
                sortOption={sortOption}
                isExpandedView={isExpandedView}
                setIsExpandedView={setIsExpandedView}
                handleScroll={handleScroll}
                isLoading={projectDropdownsByUserLoading || !selectedProjectId || isTableLoading}
                isLoadingMore={isLoadingMore}
                filteredBacklogTickets={filteredBacklogTickets}
                visibleBacklogTickets={visibleBacklogTickets}
                selectedTicketIds={selectedTicketIds}
                handleSelectAllVisible={handleSelectAllVisible}
                handleSelectTicket={handleSelectTicket}
                isAuthorized={isAuthorized}
                handleDragStart={handleDragStart}
                checkboxProps={checkboxProps}
                formatDateRange={formatDateRange}
                filteredMainSprintTickets={filteredMainSprintTickets}
                visibleSprintTickets={visibleSprintTickets}
              />
            </div>

            {/* Right Pane: Sprint plan side panels */}
            <BacklogSidebar currentUserInitials={currentUserInitials} 
              isLoading={projectDropdownsByUserLoading || !selectedProjectId || dashboardLoading || !dashboard || isTableLoading}
              myAssignedBacklogCount={(apiMyTickets?.backlog?.assigned_to_me || 0) + optimisticDeltas.myTicketsBacklog}
              totalBacklogCount={((selectedCardType === BACKLOG_CARD_TYPES.MY_TICKETS ? apiMyTickets?.backlog?.total : (sprintsTickets?.backlog?.total !== undefined ? sprintsTickets.backlog.total : lastKnownTotalBacklog)) || 0) + optimisticDeltas.totalBacklog}
              isExpandedView={isExpandedView}
              selectedCardType={selectedCardType}
              sprints={sprints}
              tickets={tickets}
              dragOverCardId={dragOverCardId}
              setDragOverCardId={setDragOverCardId}
              handleDropOnTarget={handleDropOnTarget}
              formatDateRange={formatDateRange}
              currentMainView={currentMainView}
              currentMainSprintId={currentMainSprint?.id}
              setCurrentMainView={handleSetCurrentMainView}
              getFilteredTicketsForScope={getFilteredTicketsForScope}
              isAuthorized={isAuthorized}
              handleDragStart={handleDragStart}
            />
            </div>
          )}
        </div>

        {/* Create Ticket Modal Wrapper */}
        <AddTaskModal
          open={isAddTaskOpen}
          onClose={() => setIsAddTaskOpen(false)}
          onSubmit={handleCreateTicketSubmit}
          selectedProjectId={selectedProjectId}
          userRole={userRole}
          preselectedSprintId={currentMainView === 'backlog' ? null : currentMainSprint?.id}
          preselectedSprintName={currentMainView === 'backlog' ? null : currentMainSprint?.name}
        />

        {/* Sprints Dropdown Menu */}
        <Menu
          anchorEl={sprintMenuAnchor}
          open={Boolean(sprintMenuAnchor)}
          onClose={() => setSprintMenuAnchor(null)}
          PaperProps={{
            sx: {
              maxHeight: 250,
              width: 200,
              mt: 0.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0',
              borderRadius: '6px'
            }
          }}
        >
          {sprintDropdownLoading ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : sprintDropdownOptions.length > 0 ? (
            sprintDropdownOptions.map((s) => (
              <MenuItem 
                key={s.id} 
                onClick={() => {
                  setSprintMenuAnchor(null);
                  setSprintModalData(s);
                  setShowSprintModal(true);
                }}
              >
                <OverflowTooltip title={s.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No Sprints</MenuItem>
          )}
        </Menu>

        {/* Sort options menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
        >
          <MenuItem 
            onClick={() => handleSortSelect('DEFAULT')}
            selected={sortOption === 'DEFAULT'}
            sx={{ fontSize: '0.82rem' }}
          >
            Default Order
          </MenuItem>
          <MenuItem 
            onClick={() => handleSortSelect('PRIORITY_HIGH_TO_LOW')}
            selected={sortOption === 'PRIORITY_HIGH_TO_LOW'}
            sx={{ fontSize: '0.82rem' }}
          >
            Priority (High to Low)
          </MenuItem>
          <MenuItem 
            onClick={() => handleSortSelect('SP_LOW_TO_HIGH')}
            selected={sortOption === 'SP_LOW_TO_HIGH'}
            sx={{ fontSize: '0.82rem' }}
          >
            Story Points (Low to High)
          </MenuItem>
        </Menu>

        {/* Incomplete tickets complete warning modal */}
        <ScrumWarningModal
          isOpen={isCompleteSprintDialogOpen}
          onClose={() => {
            if (!isCompletingSprint) setIsCompleteSprintDialogOpen(false);
          }}
          title={completeSprintDialogType === 'confirmation' ? 'backlog.completeSprintConfirmTitle' : 'backlog.completeSprintWarningTitle'}
          descp={completeSprintDialogType === 'confirmation' ? 'backlog.completeSprintConfirmDesc' : 'backlog.completeSprintWarningDesc'}
          descpOptions={{ incompleteCount }}
          color={completeSprintDialogType === 'confirmation' ? COLORS.brand.primaryDark : undefined}
          icon={completeSprintDialogType === 'confirmation' ? <FlagOutlined /> : undefined}
          primaryAction={{
            text: completeSprintDialogType === 'confirmation' ? t('backlog.completeSprintButton') : t('common.proceed', 'Proceed'),
            loadingText: t('backlog.completingSprintButton', 'Completing...'),
            loading: isCompletingSprint,
            onClick: completeSprintAction,
            color: 'warning'
          }}
          secondaryAction={{ text: t('common.cancel', 'Cancel'), disabled: isCompletingSprint, onClick: () => setIsCompleteSprintDialogOpen(false) }}
        />

        {/* Blocker Capacity Dialog */}
        <ScrumWarningModal
          isOpen={isBlockerOpen}
          onClose={() => setIsBlockerOpen(false)}
          title={blockerTitle}
          descp={blockerMessage || "backlog.capacityBlockerDesc"}
        />

        <SprintModal
          open={showSprintModal}
          onClose={() => setShowSprintModal(false)}
          projectId={selectedProjectId}
          sprintData={sprintModalData}
          onSuccess={handleRefresh}
        />

      </main>
    </div>
  );
};

export default BacklogManagement;
